/**
 * B2 · CLASSROOM CHAT — local-first hooks.
 *
 * Messages arrive via sync (`my_cohort_chat` for whoever is taking the
 * course, `my_cohort_chat_teacher` for whoever moderates), so the
 * conversation is written offline and delivered on reconnect. The author's
 * identity does NOT travel with it: it's hydrated via REST with
 * `useCohortIdentities` (`GET /api/cohorts/:id/roster`), which has been the
 * client's single source of identity since plan 06 · E3.
 *
 * What's NOT here, on purpose: live presence or "is typing". 300 students
 * heartbeating every 30s would be ~15,000 row deliveries per minute for data
 * that expires in half a minute. "Last seen" is DERIVED from messages
 * already synced (`lastSeenByAuthor`), at zero cost.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  type ChatReactionEmoji,
  chatReactionId,
  classroomChatQueryKeys,
  countUnreadMessages,
  deleteChatMessageMutation,
  editChatMessageMutation,
  getCohortChatMessagesQuery,
  getCohortChatReactionsQuery,
  getMyChatReadQuery,
  linkChatMessageToThreadMutation,
  markChatReadMutation,
  reactToChatMessageMutation,
  removeChatReactionMutation,
  sendChatMessageMutation,
  setChatMessageHiddenMutation,
  setChatMessagePinnedMutation,
  setChatMutedMutation,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type { CohortChatMessage, CohortChatReaction, CohortChatRead } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** A cohort's conversation (the live window the stream delivers). */
export function useCohortChatMessages(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortChatMessage>({
    queryKey: () => classroomChatQueryKeys.messages(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortChatMessagesQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MY read mark / classroom mute. */
export function useMyChatRead(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<CohortChatRead>({
    queryKey: () => classroomChatQueryKeys.read(cohortId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      const uid = base.userId();
      return db && id && uid ? getMyChatReadQuery(db, id, uid) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Reactions in the live window (stage with its own flag: can come back empty). */
export function useCohortChatReactions(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortChatReaction>({
    queryKey: () => classroomChatQueryKeys.reactions(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortChatReactionsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * A classroom's unread counter. Computed over what's ALREADY synced (there's
 * no endpoint to ask) and used both in the tab's bubble and in the
 * classroom-list card.
 */
export function useCohortChatUnread(cohortId: Accessor<string | undefined>): Accessor<number> {
  const { userId } = useSession();
  const messages = useCohortChatMessages(cohortId);
  const read = useMyChatRead(cohortId);
  const unread = createMemo(() => {
    const uid = userId();
    if (!uid) {
      return 0;
    }
    return countUnreadMessages(messages.data ?? [], uid, read.data?.last_read_at ?? null);
  });
  return unread;
}

/** Chat write actions (all go through the server's write-path). */
export function useChatActions(
  cohortId: Accessor<string | undefined>,
  orgId?: Accessor<string | undefined>,
) {
  const base = useBase();
  const requireDb = () => {
    const db = base.drizzle();
    const id = cohortId();
    const uid = base.userId();
    if (!db || !id || !uid) {
      return null;
    }
    return { db, id, uid };
  };
  return {
    send: async (body: string) => {
      const ctx = requireDb();
      if (!ctx || !body.trim()) {
        return;
      }
      await sendChatMessageMutation(ctx.db, {
        cohortId: ctx.id,
        organizationId: orgId?.() ?? null,
        authorId: ctx.uid,
        body,
      });
    },
    edit: async (messageId: string, body: string) => {
      const ctx = requireDb();
      if (ctx) {
        await editChatMessageMutation(ctx.db, messageId, body, ctx.id);
      }
    },
    remove: async (messageId: string) => {
      const ctx = requireDb();
      if (ctx) {
        await deleteChatMessageMutation(ctx.db, messageId, ctx.id);
      }
    },
    setHidden: async (messageId: string, hidden: boolean) => {
      const ctx = requireDb();
      if (ctx) {
        await setChatMessageHiddenMutation(ctx.db, messageId, hidden, ctx.id);
      }
    },
    setPinned: async (messageId: string, pinned: boolean) => {
      const ctx = requireDb();
      if (ctx) {
        await setChatMessagePinnedMutation(ctx.db, messageId, pinned, ctx.id);
      }
    },
    linkToThread: async (messageId: string, threadId: string) => {
      const ctx = requireDb();
      if (ctx) {
        await linkChatMessageToThreadMutation(ctx.db, messageId, threadId, ctx.id);
      }
    },
    markRead: async (at?: string) => {
      const ctx = requireDb();
      if (ctx) {
        await markChatReadMutation(ctx.db, {
          cohortId: ctx.id,
          organizationId: orgId?.() ?? null,
          userId: ctx.uid,
          at,
        });
      }
    },
    setMuted: async (mutedUntil: string | null) => {
      const ctx = requireDb();
      if (ctx) {
        await setChatMutedMutation(ctx.db, {
          cohortId: ctx.id,
          organizationId: orgId?.() ?? null,
          userId: ctx.uid,
          mutedUntil,
        });
      }
    },
    /** Toggles MY reaction: the same one twice removes it. */
    toggleReaction: async (messageId: string, emoji: ChatReactionEmoji, mine: string | null) => {
      const ctx = requireDb();
      if (!ctx) {
        return;
      }
      if (mine === emoji) {
        await removeChatReactionMutation(ctx.db, messageId, ctx.uid);
        return;
      }
      await reactToChatMessageMutation(ctx.db, {
        messageId,
        cohortId: ctx.id,
        organizationId: orgId?.() ?? null,
        userId: ctx.uid,
        emoji,
      });
    },
    /** Deterministic id of my reaction (for tests and debugging). */
    reactionId: (messageId: string) => chatReactionId(messageId, base.userId() ?? ""),
  };
}
